/*
 * MEETINGS ▸ CALLS, the pure half: what the page draws from the call log
 * (src/calls/log.js) and from Fireflies' notes, so it can be tested without a
 * browser. The page itself is CallsPage.js; one call is CallView.js.
 */
import { callActionItems } from "../timelineModel.mjs";

// His calls with leads, and his calls with people the CRM doesn't know yet.
// Client stretches and internal calls are in neither (src/calls/log.js).
export const VIEWS = [
  ["leads", "Leads"],
  ["unfiled", "Not in the CRM"],
];

export const PERIODS = [
  [0, "All time"],
  [30, "Last 30 days"],
  [90, "Last 90 days"],
  [365, "Last 12 months"],
];

// How a call came to be filed where it is (call_log.match_reason).
export const REASON = {
  contact: "Filed by a guest who is a contact",
  domain: "Filed by a guest's work domain",
  meet_link: "Filed by the calendar invite on its link",
  manual: "Filed by hand",
  internal: "Only Remotion on it",
  none: "No guest is in the CRM",
};

/** Search words as the server reads them: lowercased, two characters or more, at most eight. */
export function termsOf(q) {
  return String(q || "")
    .toLowerCase()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => w.length >= 2)
    .slice(0, 8);
}

/** Text cut into runs, each marked when it is one of the terms: [{ text, hit }]. */
export function splitHits(text, terms) {
  const s = String(text || "");
  if (!s || !terms || !terms.length) return [{ text: s, hit: false }];
  const escaped = terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
  const re = new RegExp(`(${escaped.join("|")})`, "gi");
  return s
    .split(re)
    .filter((part) => part !== "")
    .map((part) => ({ text: part, hit: terms.includes(part.toLowerCase()) }));
}

export const hasAny = (text, terms) => {
  const s = String(text || "").toLowerCase();
  return terms.some((t) => s.includes(t));
};

/** Newest-first calls into month groups: [{ label, calls }]. */
export function monthGroups(calls, locale) {
  const groups = [];
  for (const c of calls || []) {
    const label = c.started_at
      ? new Date(c.started_at).toLocaleDateString(locale, { month: "long", year: "numeric" })
      : "Undated";
    if (!groups.length || groups[groups.length - 1].label !== label) groups.push({ label, calls: [] });
    groups[groups.length - 1].calls.push(c);
  }
  return groups;
}

export function dayLabel(iso, locale) {
  if (!iso) return "Undated";
  return new Date(iso).toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric" });
}

export function fullDate(iso, locale) {
  if (!iso) return "Undated";
  const d = new Date(iso);
  const date = d.toLocaleDateString(locale, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  return `${date} · ${d.toLocaleTimeString(locale, { hour: "numeric", minute: "2-digit" })}`;
}

export const minutesLabel = (m) => (m ? `${Math.round(m)} min` : null);

/** "1:02:05" or "4:07" from seconds into the call. */
export function clock(seconds) {
  const t = Math.max(0, Math.floor(seconds || 0));
  const h = Math.floor(t / 3600);
  const m = Math.floor((t % 3600) / 60);
  const s = t % 60;
  return h ? `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}` : `${m}:${String(s).padStart(2, "0")}`;
}

/** Consecutive sentences by one speaker read as one turn. */
export function turnsOf(sentences) {
  const out = [];
  for (const s of sentences || []) {
    const last = out[out.length - 1];
    if (last && last.speaker === s.speaker) last.text += ` ${s.text}`;
    else out.push({ speaker: s.speaker || null, text: s.text || "", at: s.at || 0 });
  }
  return out;
}

/** A guest's display name: their name, else the part of the email before the @. */
export const guestName = (a) => (a && (a.name || (a.email ? a.email.split("@")[0] : ""))) || "Guest";

/** Their side's first names, for a list row: "Dana, Ron +2". */
export function theirNames(attendees, max = 3) {
  const theirs = (attendees || []).filter((a) => !(a.email && /@remotion\.io$/i.test(a.email)));
  const names = theirs.map((a) => guestName(a).split(/[\s.]/)[0]).filter(Boolean);
  const shown = names.slice(0, max).map((n) => n.charAt(0).toUpperCase() + n.slice(1));
  return names.length > max ? `${shown.join(", ")} +${names.length - max}` : shown.join(", ");
}

const stripBold = (s) => String(s || "").replace(/\*\*/g, "").trim();

/**
 * Fireflies' overview as points: "- **Budget:** cut by $50K" → { label, text }.
 * A line with no label keeps its text; a paragraph with no bullets is one point.
 */
export function overviewPoints(overview) {
  const lines = String(overview || "").split("\n").map((l) => l.trim()).filter(Boolean);
  return lines.map((line) => {
    const body = line.replace(/^[-*•]\s+/, "");
    const m = body.match(/^\*\*(.+?):?\*\*:?\s*(.*)$/);
    return m ? { label: stripBold(m[1]).replace(/:$/, ""), text: stripBold(m[2]) } : { label: null, text: stripBold(body) };
  });
}

/**
 * Fireflies' notes ("shorthand bullets") as sections: a line like
 * "📊 **Budget and cuts** (15:27 - 18:20)" opens one, the lines under it are its
 * points. `from` is the section's start in seconds, for a link into the call.
 */
export function noteSections(text) {
  const sections = [];
  for (const raw of String(text || "").split("\n")) {
    const line = raw.trim();
    if (!line) continue;
    const head = line.match(/^(\p{Extended_Pictographic}[️‍\p{Extended_Pictographic}]*)?\s*\*\*(.+?)\*\*\s*(?:\((\d{1,2}:\d{2}(?::\d{2})?)\s*-\s*(\d{1,2}:\d{2}(?::\d{2})?)\))?\s*$/u);
    if (head) {
      sections.push({ icon: head[1] || null, title: stripBold(head[2]), range: head[3] ? `${head[3]} – ${head[4]}` : null, from: head[3] ? seconds(head[3]) : null, lines: [] });
      continue;
    }
    if (!sections.length) sections.push({ icon: null, title: null, range: null, from: null, lines: [] });
    sections[sections.length - 1].lines.push(stripBold(line.replace(/^[-*•]\s+/, "")));
  }
  return sections.filter((s) => s.title || s.lines.length);
}

function seconds(stamp) {
  const parts = stamp.split(":").map(Number);
  return parts.reduce((acc, n) => acc * 60 + n, 0);
}

/**
 * The names our side goes by on a call: each Remotion guest's name and the part
 * of their email before the @, lowercased. Fireflies names an action item's
 * owner by the speaker's name, so this is what tells ours from theirs.
 */
export function ourNames(attendees) {
  const out = new Set(["remotion"]);
  for (const a of attendees || []) {
    if (!(a && a.email && /@remotion\.io$/i.test(a.email))) continue;
    out.add(a.email.split("@")[0].toLowerCase());
    for (const part of String(a.name || "").toLowerCase().split(/\s+/)) if (part.length > 1) out.add(part);
  }
  return [...out];
}

const isOurs = (owner, names) => {
  const words = String(owner || "").toLowerCase().split(/[\s.]+/);
  return names.some((n) => words.includes(n) || n.split(".").includes(words[0]));
};

/**
 * Action items by owner, ours first: [{ owner, ours, items: [{ text, at }] }].
 * `at` is the moment it was said, in seconds, when Fireflies wrote one. `names`
 * is ourNames(attendees).
 */
export function actionGroups(raw, names = ["remotion"]) {
  const lines = String(raw || "").split("\n");
  const stamps = new Map();
  for (const l of lines) {
    const m = l.trim().match(/^(.*?)\s*\((\d{1,2}:\d{2}(?::\d{2})?)\)$/);
    if (m) stamps.set(stripBold(m[1].replace(/^[-*•]\s+/, "")), seconds(m[2]));
  }
  return callActionItems(raw)
    .map((g) => ({
      owner: g.owner ? stripBold(g.owner) : "No owner",
      ours: !!(g.owner && isOurs(g.owner, names)),
      items: g.items.map((text) => ({ text, at: stamps.has(text) ? stamps.get(text) : null })),
    }))
    .sort((a, b) => b.ours - a.ours);
}

/** The line a call's record card shows under the title. */
export function callFacts(c, locale) {
  return [dayLabel(c.started_at, locale), minutesLabel(c.duration_min), theirNames(c.attendees, 2)].filter(Boolean).join(" · ");
}
